
import { useState, useEffect } from 'react';

/**
 * Custom hook to detect if a background color is light or dark
 */
export function useBackgroundBrightness(backgroundInfo: { url?: string; smallUrl?: string } | null) {
  const [isLight, setIsLight] = useState(false);

  useEffect(() => {
    if (!backgroundInfo) {
      setIsLight(false); // Default to dark if no background info
      return;
    }

    // Function to determine if a color is light
    const checkBrightness = () => {
      try {
        // Create a small canvas to sample background image
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          console.error('Failed to get canvas context');
          return false; // Default to dark on error
        }

        // Create a temp image to load the background
        const img = new Image();
        img.crossOrigin = 'Anonymous'; // Allow loading from different origins
        img.src = backgroundInfo.smallUrl || backgroundInfo.url || '';

        img.onload = () => {
          // Draw image onto canvas
          canvas.width = img.width;
          canvas.height = img.height;
          context.drawImage(img, 0, 0, img.width, img.height);

          try {
            // Sample from the center of the image (most likely area behind the logo)
            const centerX = Math.floor(canvas.width / 2);
            const centerY = Math.floor(canvas.height / 3); // Sample from upper third where logo is
            
            // Sample a small area (10x10 pixels)
            const sampleSize = 10;
            const imageData = context.getImageData(
              centerX - sampleSize / 2, 
              centerY - sampleSize / 2, 
              sampleSize, 
              sampleSize
            );
            
            // Calculate average RGB
            let r = 0, g = 0, b = 0;
            for (let i = 0; i < imageData.data.length; i += 4) {
              r += imageData.data[i];
              g += imageData.data[i + 1];
              b += imageData.data[i + 2];
            }
            
            const pixels = imageData.data.length / 4;
            r = Math.floor(r / pixels);
            g = Math.floor(g / pixels);
            b = Math.floor(b / pixels);
            
            // Calculate luminance using the formula: 0.299*R + 0.587*G + 0.114*B
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
            
            // Threshold for determining light vs dark (186 is middle of 8-bit range)
            const isBackgroundLight = luminance > 186;
            
            setIsLight(isBackgroundLight);
            console.log(`Background luminance: ${luminance}, isLight: ${isBackgroundLight}`);
          } catch (error) {
            console.error('Error analyzing image data:', error);
            setIsLight(false); // Default to dark on error
          }
        };
        
        img.onerror = () => {
          console.error('Error loading background image for brightness analysis');
          setIsLight(false); // Default to dark on error
        };
      } catch (error) {
        console.error('Error in brightness detection:', error);
        setIsLight(false); // Default to dark on error
      }
    };
    
    // Run the brightness check
    checkBrightness();
  }, [backgroundInfo]);

  return { isLight };
}
